import type { ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/Dialogs';
import { HelpTip } from '../../Experiments/Details/ExperimentSettings/TargetingHelp';
import { CreateTokenDialog } from './CreateTokenDialog';
import { LoadFailed } from './LoadFailed';
import { TokensTable } from './TokensTable';
import { AGENT_ACCESS_COPY } from './agent-access-copy';
import { useAgentAccess } from './use-agent-access';

const COPY = AGENT_ACCESS_COPY;

type TokensCardProps = {
  title: string;
  /** What the card is for, behind the ⓘ beside its title. */
  help: string;
  actions?: ReactNode;
  tourAnchor: string;
  children: ReactNode;
};

/** The card of Settings, Team, class for class, with an ⓘ on the title as every card now has. */
const TokensCard = ({ title, help, actions, tourAnchor, children }: TokensCardProps) => (
  <section className="bg-panel border-line rounded-panel border" data-tour={tourAnchor}>
    <div className="border-line flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <h2 className="text-foreground m-0 min-w-0 truncate text-body font-medium" title={title}>
          {title}
        </h2>
        <HelpTip text={help} label={title.toLowerCase()} />
      </div>
      {!!actions && <div className="flex flex-col gap-2 md:shrink-0 md:flex-row md:items-center">{actions}</div>}
    </div>
    <div className="p-3">{children}</div>
  </section>
);

/**
 * Settings, Agent access (PER-328): personal access tokens an AI agent uses to work in Pertento as
 * the person who made them. Built as Settings, Team is: the list first with its primary action in
 * the card's header, and the making of one in a dialog. Below them, the apps connected over OAuth
 * (PER-334), such as Claude or ChatGPT, each disconnectable. An Owner or Admin also sees every token
 * and connection made in their organization and can revoke any of them.
 *
 * The secret is shown once, as the dialog's second step, and nowhere else: no list carries it.
 */
export function SettingsAgentAccess() {
  const manager = useAgentAccess();
  const { tokens, isLoading, isError, retry, now, askRevoke, openCreate, options } = manager;
  const app = manager.revoking?.kind === 'oauth';

  return (
    <div className="flex flex-col gap-[14px]">
      <TokensCard
        title={COPY.tokensTitle}
        help={COPY.tokensDescription}
        tourAnchor="settings-agent-access-tokens"
        actions={
          <Button
            onClick={openCreate}
            className="h-9 w-full rounded-lg md:w-auto"
            data-tour="settings-agent-access-create"
          >
            <Plus className="h-4 w-4" />
            {COPY.newToken}
          </Button>
        }
      >
        {isError ? (
          <LoadFailed onRetry={retry} />
        ) : isLoading ? null : (
          <TokensTable
            tokens={tokens}
            now={now}
            label={COPY.tableLabel}
            emptyText={COPY.empty}
            emptyAction={
              <Button onClick={openCreate} className="mt-4 h-9 rounded-lg">
                <Plus className="mr-2 h-4 w-4" />
                {COPY.newToken}
              </Button>
            }
            websites={options?.websites}
            onRevoke={askRevoke}
          />
        )}
      </TokensCard>

      <TokensCard title={COPY.appsTitle} help={COPY.appsDescription} tourAnchor="settings-agent-access-apps">
        {isError ? (
          <LoadFailed onRetry={retry} />
        ) : isLoading ? null : (
          <TokensTable
            tokens={manager.connectedApps}
            now={now}
            label={COPY.appsTableLabel}
            emptyText={COPY.appsEmpty}
            websites={options?.websites}
            onRevoke={askRevoke}
          />
        )}
      </TokensCard>

      {manager.canManageTeam && (
        <TokensCard title={COPY.teamTitle} help={COPY.teamDescription} tourAnchor="settings-agent-access-team">
          {manager.isTeamError ? (
            <LoadFailed onRetry={retry} />
          ) : manager.isTeamLoading ? null : (
            <TokensTable
              tokens={manager.teamTokens}
              now={now}
              label={COPY.teamTableLabel}
              emptyText={COPY.teamEmpty}
              showOwner
              websites={options?.websites}
              onRevoke={askRevoke}
            />
          )}
        </TokensCard>
      )}

      <CreateTokenDialog manager={manager} />

      <ConfirmDialog
        open={!!manager.revoking}
        title={(app ? COPY.disconnectTitle : COPY.revokeTitle)(manager.revoking?.name ?? '')}
        confirmLabel={app ? COPY.disconnect : COPY.revoke}
        confirmVariant="destructive"
        onConfirm={manager.confirmRevoke}
        onCancel={manager.cancelRevoke}
      >
        <p className="supporting m-0">{app ? COPY.disconnectBody : COPY.revokeBody}</p>
      </ConfirmDialog>
    </div>
  );
}
